import { computed, mergeProps, reactive, toValue, unref } from 'vue';
import { useI18n } from 'vue-i18n';

import { formatDateTimeWithMilitarySupport, LuxonDateTimeFormats } from '@nova/core';
import { nestedCompanyPresenterDataProps } from '@satellite/composables/presenterV2NestedCompanyProps';
import { EntityPresenterV2Mode } from '@satellite/composables/presenterV2EntityMode';
import {
  type PresenterV2DisplayConfigOptions,
  type PresenterV2DisplayOverrideProps,
  type PresenterV2DisplaySize
} from '@satellite/composables/presenterV2DisplayOptions';
import { reactiveOption } from '@satellite/composables/presenterV2ReactiveOption';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type { AppointmentDetails } from './appointmentPresenterV2Shared';

export type AppointmentDisplayField =
  | 'carrierCompany'
  | 'confirmationNumber'
  | 'date'
  | 'end'
  | 'refNumbers'
  | 'start'
  | 'status'
  | 'statusChip'
  | 'timeInterval';

export interface UseAppointmentDisplayV2Options extends PresenterV2DisplayConfigOptions {
  carrierCompanyProps?: PresenterV2DisplayOverrideProps;
  confirmationNumberProps?: PresenterV2DisplayOverrideProps;
  dateProps?: PresenterV2DisplayOverrideProps;
  endProps?: PresenterV2DisplayOverrideProps;
  refNumbersProps?: PresenterV2DisplayOverrideProps;
  startProps?: PresenterV2DisplayOverrideProps;
  statusProps?: PresenterV2DisplayOverrideProps;
  timeIntervalProps?: PresenterV2DisplayOverrideProps;
}

function chipSizeForDisplay(size: PresenterV2DisplaySize) {
  return size === 'small' ? 'x-small' : 'small';
}

function chipTextTypographyClass(size: PresenterV2DisplaySize) {
  return {
    'typography-chip-sm': size === 'small',
    'typography-caption-bold': ['regular', 'large'].includes(size)
  };
}

function formatAppointmentDate(dateTime: string | Date, warehouseTimezone: Maybe<string>) {
  const value = dateTime instanceof Date ? dateTime.toISOString() : dateTime;
  return formatDateTimeWithMilitarySupport(
    value,
    warehouseTimezone,
    LuxonDateTimeFormats.DateWithAbbreviatedMonth,
    false,
    LuxonDateTimeFormats.DateWithAbbreviatedMonth
  );
}

function formatAppointmentTime(dateTime: string | Date, warehouseTimezone: Maybe<string>) {
  const value = dateTime instanceof Date ? dateTime.toISOString() : dateTime;
  return formatDateTimeWithMilitarySupport(
    value,
    warehouseTimezone,
    LuxonDateTimeFormats.Extended12HrTimeAMPM,
    false,
    LuxonDateTimeFormats.Extended24HrTime
  );
}

export function useAppointmentDisplayV2(
  appointment: Ref<AppointmentDetails | null> | ComputedRef<AppointmentDetails | null>,
  options: UseAppointmentDisplayV2Options = {}
) {
  const { t } = useI18n();

  const displaySize = reactiveOption(options.size, 'regular');
  const displayMode = reactiveOption(options.mode, EntityPresenterV2Mode.Live);
  const effectiveTimezone = computed(() => toValue(options.warehouseTimezone) ?? null);
  const isFetchingValue = computed(() =>
    options.isFetching ? unref(options.isFetching) : false
  );

  const confirmationNumber = computed(() => appointment.value?.confirmationNumber ?? null);
  const refNumbers = computed(() => {
    if (Array.isArray(appointment.value?.refNumbers) && appointment.value.refNumbers.length > 0) {
      return appointment.value.refNumbers.join(', ');
    }

    return appointment.value?.refNumber || null;
  });
  const status = computed(() => appointment.value?.status ?? null);
  const statusLabel = computed(() =>
    status.value ? t(`global.appointmentStatuses.${status.value}`) : null
  );
  const start = computed(() => appointment.value?.start ?? null);
  const end = computed(() => appointment.value?.end ?? null);
  const date = computed(() => appointment.value?.start ?? null);

  const carrier = computed(() => appointment.value?.user ?? null);
  const carrierId = computed(() => carrier.value?.id ?? appointment.value?.userId ?? null);
  const carrierCompany = computed(() => carrier.value?.company ?? null);
  const carrierCompanyId = computed(
    () => carrierCompany.value?.id ?? carrier.value?.companyId ?? null
  );

  const dateFormatted = computed(() =>
    date.value ? formatAppointmentDate(date.value, effectiveTimezone.value) : null
  );
  const startFormatted = computed(() =>
    start.value ? formatAppointmentTime(start.value, effectiveTimezone.value) : null
  );
  const endFormatted = computed(() =>
    end.value ? formatAppointmentTime(end.value, effectiveTimezone.value) : null
  );

  const carrierCompanyPresenterProps = computed(() =>
    mergeProps(
      nestedCompanyPresenterDataProps(displayMode.value, {
        carrier: carrier.value,
        company: carrierCompany.value
      }),
      toValue(options.carrierCompanyProps) ?? {}
    )
  );

  const confirmationNumberAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'appointment-presenter-confirmation-number' },
      toValue(options.confirmationNumberProps) ?? {}
    )
  );

  const refNumbersAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'appointment-presenter-ref-numbers' },
      toValue(options.refNumbersProps) ?? {}
    )
  );

  const dateAttrs = computed(() =>
    mergeProps({ 'data-testid': 'appointment-presenter-date' }, toValue(options.dateProps) ?? {})
  );

  const startAttrs = computed(() =>
    mergeProps({ 'data-testid': 'appointment-presenter-start' }, toValue(options.startProps) ?? {})
  );

  const endAttrs = computed(() =>
    mergeProps({ 'data-testid': 'appointment-presenter-end' }, toValue(options.endProps) ?? {})
  );

  const timeIntervalAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'appointment-presenter-time-interval' },
      toValue(options.timeIntervalProps) ?? {}
    )
  );

  const statusAttrs = computed(() =>
    mergeProps({ 'data-testid': 'appointment-presenter-status' }, toValue(options.statusProps) ?? {})
  );

  const statusChipAttrs = computed(() => ({
    size: chipSizeForDisplay(displaySize.value),
    color: String(status.value ?? '').toLowerCase(),
    pill: true,
    'data-testid': 'appointment-presenter-status-chip-shell'
  }));

  const statusChipTextAttrs = computed(() => ({
    class: chipTextTypographyClass(displaySize.value),
    'data-testid': 'appointment-presenter-status-chip'
  }));

  return reactive({
    appointment,
    mode: displayMode,
    size: displaySize,
    warehouseTimezone: effectiveTimezone,
    isFetching: isFetchingValue,
    confirmationNumber,
    refNumbers,
    status,
    statusLabel,
    start,
    end,
    date,
    carrier,
    carrierId,
    carrierCompany,
    carrierCompanyId,
    dateFormatted,
    startFormatted,
    endFormatted,
    confirmationNumberAttrs,
    refNumbersAttrs,
    dateAttrs,
    startAttrs,
    endAttrs,
    timeIntervalAttrs,
    statusAttrs,
    statusChipAttrs,
    statusChipTextAttrs,
    carrierCompanyPresenterProps
  });
}

export type AppointmentDisplayV2Data = ReturnType<typeof useAppointmentDisplayV2>;
